import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Ticket, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useBranding } from '../../context/BrandingContext';
import { staffApi } from '../../services/api';
import OfflineBanner from '../../components/OfflineBanner';

/**
 * Staff: list of all active ticketed events. From here the operator picks
 * an event to view real-time per-tier availability and start a cash sale.
 */
export default function StaffEvents() {
  const navigate = useNavigate();
  const { primary_color } = useBranding();
  const pc = primary_color || '#D44E20';
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    staffApi.get('/staff/events')
      .then(r => { if (alive) setEvents(r.data?.events || []); })
      .catch(e => { if (alive) setError(e.response?.data?.detail || 'Errore nel caricamento eventi'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' });
    } catch { return iso; }
  };

  return (
    <div className="staff-app flex flex-col min-h-dvh bg-[#0E0E12]">
      <OfflineBanner />

      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center gap-3 animate-fade-in-up">
        <button
          onClick={() => navigate('/staff/home')}
          data-testid="staff-events-back-btn"
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-[#888] hover:text-white hover:bg-white/10 transition-all border border-white/5"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="text-[#888888] text-xs">Eventi</p>
          <h1 className="text-white font-bold text-xl leading-tight">Disponibilità & vendita</h1>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 pb-8 flex-1">
        {loading && (
          <div className="flex items-center justify-center py-20 text-[#888]" data-testid="staff-events-loading">
            <Loader2 className="animate-spin mr-2" size={18} /> Caricamento…
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 text-red-300 flex items-start gap-3" data-testid="staff-events-error">
            <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && events.length === 0 && (
          <div className="bg-[#18181E] border border-white/5 rounded-2xl p-8 text-center" data-testid="staff-events-empty">
            <Ticket size={32} className="mx-auto text-[#444] mb-3" />
            <p className="text-white font-semibold text-base">Nessun evento attivo</p>
            <p className="text-[#888] text-sm mt-1">
              Crea un evento con biglietti dal pannello admin per iniziare la vendita.
            </p>
          </div>
        )}

        {!loading && !error && events.length > 0 && (
          <div className="space-y-3" data-testid="staff-events-list">
            {events.map((ev, idx) => {
              const soldOut = ev.capacity_total !== null && ev.tickets_sold_total >= ev.capacity_total;
              const remaining = ev.capacity_total !== null
                ? Math.max(0, ev.capacity_total - ev.tickets_sold_total)
                : null;
              return (
                <button
                  key={ev.id}
                  data-testid={`staff-event-card-${ev.id}`}
                  onClick={() => navigate(`/staff/events/${ev.id}`)}
                  className={`w-full text-left bg-[#18181E] border border-white/5 rounded-2xl p-4 active:scale-[0.98] transition-all animate-fade-in-up stagger-${(idx % 5) + 1} hover:border-white/10`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-white font-bold text-base leading-snug truncate" data-testid={`staff-event-name-${ev.id}`}>
                        {ev.name}
                      </h2>
                      <div className="flex items-center gap-3 mt-1.5 text-[#888] text-xs">
                        <span className="inline-flex items-center gap-1">
                          <Calendar size={12} /> {formatDate(ev.event_date) || '—'}
                        </span>
                        {ev.event_time && (
                          <span className="inline-flex items-center gap-1">
                            <Clock size={12} /> {ev.event_time}
                          </span>
                        )}
                        {ev.is_past && (
                          <span className="px-1.5 py-0.5 rounded bg-white/10 text-[10px] uppercase text-[#aaa]">Passato</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-white font-bold text-lg tabular-nums" data-testid={`staff-event-sold-${ev.id}`}>
                        {ev.tickets_sold_total}
                        {ev.capacity_total !== null && (
                          <span className="text-[#666] text-sm font-normal">/{ev.capacity_total}</span>
                        )}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide" style={{ color: soldOut ? '#ef4444' : '#888' }}>
                        {ev.has_uncapped
                          ? 'venduti'
                          : soldOut
                            ? 'esaurito'
                            : `${remaining} disponibili`}
                      </p>
                    </div>
                  </div>

                  {/* Progress bar (only when capped) */}
                  {ev.capacity_total !== null && ev.capacity_total > 0 && (
                    <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, (ev.tickets_sold_total / ev.capacity_total) * 100)}%`,
                          backgroundColor: soldOut ? '#ef4444' : pc,
                        }}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
